/**
 * Opportunity Planning Tab
 * Unified Scenario Planning for Opportunities
 * 
 * Features:
 * - Scenario Bar (baseline/scenario selection, create, compare, activate)
 * - Basis toggle: Per Unit | Annualized | Lifetime
 * - Executive tiles: Total Revenue, Revenue Delta, Volume Delta, Avg Unit Price
 * - Parts rollup table (sorted by largest delta)
 * - Coverage meter showing X of Y parts edited
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  TrendingUp, TrendingDown, Package, Target, DollarSign,
  BarChart3, CheckCircle2, Circle, Layers, Plus, GitCompare,
  Play, AlertCircle, ChevronDown, ChevronRight, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';

// Format currency compactly
const formatCurrency = (cents, compact = false) => {
  if (cents === null || cents === undefined) return '$0';
  const dollars = cents / 100;
  
  if (compact && Math.abs(dollars) >= 1000000) {
    return `$${(dollars / 1000000).toFixed(1)}M`;
  } else if (compact && Math.abs(dollars) >= 1000) {
    return `$${(dollars / 1000).toFixed(0)}K`;
  }
  
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(dollars);
};

// Format delta with sign and color
const formatDelta = (cents, compact = false) => {
  if (cents === null || cents === undefined) return null;
  const isPositive = cents >= 0;
  const formatted = formatCurrency(Math.abs(cents), compact);
  return {
    text: `${isPositive ? '+' : '-'}${formatted}`,
    isPositive,
    value: cents
  };
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Format volume compactly
const formatVolume = (volume, compact = false) => {
  if (volume === null || volume === undefined) return '0';
  if (compact && Math.abs(volume) >= 1000000) {
    return `${(volume / 1000000).toFixed(1)}M`;
  } else if (compact && Math.abs(volume) >= 1000) {
    return `${(volume / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US').format(volume);
};

// Basis options
const BASIS_OPTIONS = [
  { value: 'per_unit', label: 'Per Unit', requiresVolume: true },
  { value: 'annualized', label: 'Annualized', requiresVolume: false },
  { value: 'lifetime', label: 'Lifetime (aligned)', requiresVolume: false }
];

const OpportunityPlanningTab = ({ opportunityId, opportunity, onUpdate }) => {
  const queryClient = useQueryClient();
  
  // State
  const [selectedBasis, setSelectedBasis] = useState('annualized');
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');
  const [expandedParts, setExpandedParts] = useState({});

  // Fetch planning data
  const { data: planningData, isLoading, error, refetch } = useQuery({
    queryKey: ['opportunityPlanning', opportunityId, selectedBasis],
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/planning/opportunities/${opportunityId}/planning?basis=${selectedBasis}`
      );
      return response.data;
    },
    enabled: !!opportunityId,
    refetchOnWindowFocus: false
  });

  // Derived initial scenario ID (avoid useEffect for setState)
  const effectiveScenarioId = selectedScenarioId ?? (() => {
    if (planningData?.active_scenario_id) {
      return planningData.active_scenario_id;
    }
    if (planningData?.scenarios?.length > 0) {
      return planningData.scenarios[0].id;
    }
    return null;
  })();

  // Get selected scenario
  const selectedScenario = planningData?.scenarios?.find(s => s.id === effectiveScenarioId);
  const isActiveScenario = effectiveScenarioId === planningData?.active_scenario_id;

  // Check if Per Unit basis is available (needs volume > 0)
  const hasVolume = planningData?.total_volume_baseline > 0 || planningData?.total_volume_scenario > 0;

  // Create scenario mutation
  const createScenarioMutation = useMutation({
    mutationFn: async ({ name, description }) => {
      const response = await apiClient.post('/api/planning/scenarios', {
        opportunity_id: opportunityId,
        name,
        description,
        scope_type: 'entire_opportunity',
        affected_part_ids: planningData?.parts_summary?.map(p => p.part_id) || []
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['opportunityPlanning', opportunityId]);
      setShowCreateDialog(false);
      setNewScenarioName('');
      setNewScenarioDescription('');
      if (data.scenario?.id) {
        setSelectedScenarioId(data.scenario.id);
      }
    }
  });

  // Activate scenario mutation
  const activateScenarioMutation = useMutation({
    mutationFn: async (scenarioId) => {
      const response = await apiClient.post(
        `/api/planning/opportunities/${opportunityId}/scenarios/${scenarioId}/activate`,
        { confirmation: true }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['opportunityPlanning', opportunityId]);
      setShowActivateConfirm(false);
      if (onUpdate) onUpdate();
    }
  });

  // Handle basis change
  const handleBasisChange = (value) => {
    if (value === 'per_unit' && !hasVolume) return;
    setSelectedBasis(value);
  };

  // Handle create scenario
  const handleCreateScenario = () => {
    if (!newScenarioName.trim()) return;
    createScenarioMutation.mutate({
      name: newScenarioName.trim(),
      description: newScenarioDescription.trim() || null
    });
  };

  // Handle activate scenario
  const handleActivateScenario = () => {
    if (!effectiveScenarioId) return;
    activateScenarioMutation.mutate(effectiveScenarioId);
  };

  // Toggle part expansion
  const togglePartExpansion = (partId) => {
    setExpandedParts(prev => ({
      ...prev,
      [partId]: !prev[partId]
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
        <p>Failed to load planning data</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const {
    opportunity_name,
    active_scenario_name,
    scenarios = [],
    total_revenue_baseline_cents = 0,
    total_revenue_scenario_cents = 0,
    revenue_delta_cents = 0,
    revenue_delta_percent = 0,
    total_volume_baseline = 0,
    total_volume_scenario = 0,
    volume_delta = 0,
    avg_unit_price_cents = 0,
    total_parts = 0,
    edited_parts = 0,
    parts_summary = []
  } = planningData || {};

  const coveragePercent = total_parts > 0 ? (edited_parts / total_parts) * 100 : 0;
  const isPerUnitBasis = selectedBasis === 'per_unit';

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Scenario Bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Left: Active Scenario & Selector */}
            <div className="flex items-center gap-3">
              {/* Active Scenario Badge */}
              {active_scenario_name ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Active: {active_scenario_name}
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <Circle className="w-3 h-3" />
                  Using Baseline
                </Badge>
              )}

              {/* Scenario Selector */}
              <Select 
                value={effectiveScenarioId || 'baseline'} 
                onValueChange={(v) => setSelectedScenarioId(v === 'baseline' ? null : v)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue placeholder="Select scenario">
                    {effectiveScenarioId ? (
                      selectedScenario?.name || 'Loading...'
                    ) : (
                      'Baseline'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baseline">
                    <span className="flex items-center gap-2">
                      <Circle className="w-3 h-3 text-slate-400" />
                      Baseline
                    </span>
                  </SelectItem>
                  {scenarios.map((scenario) => (
                    <SelectItem key={scenario.id} value={scenario.id}>
                      <span className="flex items-center gap-2">
                        {scenario.is_active_for_opportunity ? (
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <Circle className="w-3 h-3 text-slate-400" />
                        )}
                        {scenario.name}
                        {scenario.coverage_percent > 0 && (
                          <span className="text-xs text-slate-500">
                            ({scenario.coverage_percent.toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="w-4 h-4" />
                Create
              </Button>
              
              {effectiveScenarioId && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-1"
                    onClick={() => {
                      // Navigate to Planning Hub with scenario pre-selected for comparison
                      window.location.href = `/planning?compare=${effectiveScenarioId}`;
                    }}
                  >
                    <GitCompare className="w-4 h-4" />
                    Compare
                  </Button>
                  
                  {!isActiveScenario && (
                    <Button 
                      size="sm" 
                      className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowActivateConfirm(true)}
                    >
                      <Play className="w-4 h-4" />
                      Activate
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Basis Toggle */}
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">View:</span>
              <div className="flex bg-slate-100 rounded-md p-0.5">
                {BASIS_OPTIONS.map((option) => {
                  const disabled = option.requiresVolume && !hasVolume;
                  return (
                    <Tooltip key={option.value}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleBasisChange(option.value)}
                          disabled={disabled}
                          className={cn(
                            "px-3 py-1 text-xs font-medium rounded transition-colors",
                            selectedBasis === option.value
                              ? "bg-white text-slate-900 shadow-sm"
                              : disabled
                              ? "text-slate-400 cursor-not-allowed"
                              : "text-slate-600 hover:text-slate-900"
                          )}
                        >
                          {option.label}
                        </button>
                      </TooltipTrigger>
                      {disabled && (
                        <TooltipContent>
                          <p>Per Unit view requires volume data</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Executive Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Total Revenue / Unit Price */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
              <DollarSign className="w-4 h-4" />
              {isPerUnitBasis ? 'Weighted Unit Price' : 'Total Revenue'}
            </div>
            <div className="text-xl font-bold text-slate-900">
              {formatCurrency(isActiveScenario ? total_revenue_scenario_cents : total_revenue_baseline_cents, !isPerUnitBasis)}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {selectedBasis === 'annualized' && 'Annualized'}
              {selectedBasis === 'lifetime' && 'Lifetime (aligned)'}
              {selectedBasis === 'per_unit' && 'Per Unit'}
            </div>
          </div>

          {/* Revenue Delta */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
              {revenue_delta_cents >= 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500" />
              )}
              {isPerUnitBasis ? 'Unit Price Delta' : 'Revenue Delta'}
            </div>
            <div className={cn(
              "text-xl font-bold",
              revenue_delta_cents >= 0 ? "text-emerald-600" : "text-red-600"
            )}>
              {formatDelta(revenue_delta_cents, !isPerUnitBasis)?.text || '$0'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {isPerUnitBasis ? 'Weighted scenario vs baseline' : `${formatPercent(revenue_delta_percent)} vs baseline`}
            </div>
          </div>

          {/* Volume Delta (only if exists) */}
          {hasVolume && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <BarChart3 className="w-4 h-4" />
                Volume Delta
              </div>
              <div className={cn(
                "text-xl font-bold",
                volume_delta >= 0 ? "text-emerald-600" : "text-red-600"
              )}>
                {volume_delta >= 0 ? '+' : ''}{formatVolume(volume_delta, true)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {formatVolume(total_volume_scenario, true)} total
              </div>
            </div>
          )}

          {/* Avg Unit Price (only if has volume) */}
          {hasVolume && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
                <Target className="w-4 h-4" />
                Avg Unit Price
              </div>
              <div className="text-xl font-bold text-slate-900">
                {formatCurrency(avg_unit_price_cents)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {isPerUnitBasis ? 'Selected scenario' : 'Revenue / Volume'}
              </div>
            </div>
          )}
        </div>

        {/* Coverage Meter */}
        {effectiveScenarioId && selectedScenario && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">
                  Coverage: {edited_parts} of {total_parts} parts edited
                </span>
              </div>
              <span className="text-sm font-medium text-slate-900">
                {coveragePercent.toFixed(0)}%
              </span>
            </div>
            <Progress value={coveragePercent} className="h-2" />
            <p className="text-xs text-slate-500 mt-2">
              Unedited parts inherit baseline values
            </p>
          </div>
        )}

        {/* Parts Rollup Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Parts Rollup
              <span className="text-slate-500 font-normal">
                (sorted by largest delta)
              </span>
            </h3>
          </div>

          {parts_summary.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No parts linked to this opportunity</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-200">
                <div className="col-span-4">Part</div>
                <div className="col-span-2 text-right">{isPerUnitBasis ? 'Baseline Unit' : 'Baseline'}</div>
                <div className="col-span-2 text-right">{isPerUnitBasis ? 'Scenario Unit' : 'Scenario'}</div>
                <div className="col-span-2 text-right">{isPerUnitBasis ? 'Unit Delta' : 'Delta'}</div>
                <div className="col-span-2 text-center">Status</div>
              </div>

              {/* Rows */}
              {parts_summary.map((part) => {
                const isExpanded = expandedParts[part.part_id];
                const delta = formatDelta(part.revenue_delta_cents, !isPerUnitBasis);
                
                return (
                  <div key={part.part_id}>
                    <div 
                      className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => togglePartExpansion(part.part_id)}
                    >
                      <div className="col-span-4 flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {part.part_number}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {part.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="col-span-2 text-right self-center">
                        <span className="text-sm text-slate-600">
                          {formatCurrency(part.baseline_revenue_cents, true)}
                        </span>
                      </div>
                      
                      <div className="col-span-2 text-right self-center">
                        <span className="text-sm font-medium text-slate-900">
                          {formatCurrency(part.scenario_revenue_cents, true)}
                        </span>
                      </div>
                      
                      <div className="col-span-2 text-right self-center">
                        {delta && (
                          <span className={cn(
                            "text-sm font-medium",
                            delta.isPositive ? "text-emerald-600" : "text-red-600"
                          )}>
                            {delta.text}
                          </span>
                        )}
                      </div>
                      
                      <div className="col-span-2 flex justify-center self-center">
                        {part.is_edited ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                            Edited
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            Inherits baseline
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1">Unit Price</p>
                            <p className="text-slate-900">
                              Baseline: {formatCurrency(part.baseline_piece_price_cents)}
                            </p>
                            <p className="text-slate-900">
                              Scenario: {formatCurrency(part.scenario_piece_price_cents)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1">Volume</p>
                            <p className="text-slate-900">
                              Baseline: {formatVolume(part.baseline_volume_total)}
                            </p>
                            <p className="text-slate-900">
                              Scenario: {formatVolume(part.scenario_volume_total)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-slate-500 mb-1">SOP / EOP</p>
                            <p className="text-slate-900">
                              {part.sop_date || 'N/A'} - {part.eop_date || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Navigate to part detail using part_number (not part_id UUID)
                            window.location.href = `/parts/${part.part_number}?opp=${opportunityId}&scenario=${effectiveScenarioId || ''}`;
                          }}
                        >
                          Edit Part in Scenario
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create Scenario Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Scenario</DialogTitle>
              <DialogDescription>
                Create a scenario to model different pricing or volume assumptions for this opportunity.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Scenario Name *
                </label>
                <Input
                  value={newScenarioName}
                  onChange={(e) => setNewScenarioName(e.target.value)}
                  placeholder="e.g., Price Increase Q2, Volume Ramp Scenario"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Description
                </label>
                <Textarea
                  value={newScenarioDescription}
                  onChange={(e) => setNewScenarioDescription(e.target.value)}
                  placeholder="Brief description of this scenario..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateScenario}
                disabled={!newScenarioName.trim() || createScenarioMutation.isPending}
              >
                {createScenarioMutation.isPending ? 'Creating...' : 'Create Scenario'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Activate Scenario Confirmation */}
        <AlertDialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Activate Scenario for Opportunity?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  <strong>Activate &quot;{selectedScenario?.name}&quot;</strong> for opportunity <strong>{opportunity_name}</strong>?
                </p>
                <p className="text-amber-600">
                  ⚠️ This will update rollups for all parts in this opportunity. 
                  Pipeline totals will reflect this scenario&apos;s values.
                </p>
                {selectedScenario && (
                  <div className="bg-slate-50 rounded p-3 mt-3">
                    <p className="text-sm">
                      Coverage: {selectedScenario.edited_parts_count} of {selectedScenario.total_parts_count} parts edited
                    </p>
                    <p className="text-sm">
                      Revenue impact: {formatDelta(selectedScenario.revenue_delta_cents, true)?.text || '$0'}
                    </p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleActivateScenario}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {activateScenarioMutation.isPending ? 'Activating...' : 'Activate Scenario'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Alignment Mode Note */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Info className="w-3 h-3" />
          <span>Aligned to overlap period</span>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default OpportunityPlanningTab;
